import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { usePopup } from '../../../contexts/PopupContext';
import { UserListAvatar } from '../../common/UserListAvatar';
import {
  formatProfileLastSeenPhrase,
  getProfileLastSeenLampTone,
} from '../../../utils/formatProfileLastSeen';
import {
  getFriends,
  acceptFriendInvitation,
  rejectFriendInvitation,
  cancelFriendInvitation,
  removeFriend
} from '../../../utils/friendsService';
import Loading from '../../layout/Loading';
import './FriendsManagementPopup.css';

function FriendsManagementPopup({ onClose }) {
  const { t, i18n } = useTranslation(['profile', 'shared', 'events']);
  const { openPopup } = usePopup();
  const [activeTab, setActiveTab] = useState('received'); // 'received', 'sent', 'friends'
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [listQuery, setListQuery] = useState('');
  const [listSort, setListSort] = useState('recent'); // 'recent' | 'alpha'
  const channelRef = useRef(null);

  const getUserDisplayName = useCallback(
    (user) =>
      user.nickname ||
      (user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : user.name || t('profile.info.nameNotSet')),
    [t],
  );

  const matchesUserQuery = useCallback((user, q) => {
    const needle = (q || '').trim().toLowerCase();
    if (!needle) return true;
    const parts = [user.nickname, user.first_name, user.last_name, user.name, user.city]
      .filter(Boolean)
      .map((x) => String(x).toLowerCase());
    return parts.some((p) => p.includes(needle));
  }, []);

  const processedFriends = useMemo(() => {
    let rows = friends.filter((u) => matchesUserQuery(u, listQuery));
    const byRecent = (a, b) => {
      const ta = a.last_seen_at
        ? new Date(a.last_seen_at).getTime()
        : a.friendship_created_at
          ? new Date(a.friendship_created_at).getTime()
          : 0;
      const tb = b.last_seen_at
        ? new Date(b.last_seen_at).getTime()
        : b.friendship_created_at
          ? new Date(b.friendship_created_at).getTime()
          : 0;
      if (tb !== ta) return tb - ta;
      return getUserDisplayName(a).localeCompare(getUserDisplayName(b), undefined, { sensitivity: 'base' });
    };
    const byAlpha = (a, b) =>
      getUserDisplayName(a).localeCompare(getUserDisplayName(b), undefined, { sensitivity: 'base' });
    rows = [...rows].sort(listSort === 'alpha' ? byAlpha : byRecent);
    return rows;
  }, [friends, listQuery, listSort, matchesUserQuery, getUserDisplayName]);

  const processedReceived = useMemo(() => {
    let rows = receivedInvitations.filter((inv) => matchesUserQuery(inv.sender, listQuery));
    const byRecent = (a, b) => {
      const ua = a.sender;
      const ub = b.sender;
      const ta = ua.last_seen_at
        ? new Date(ua.last_seen_at).getTime()
        : a.created_at
          ? new Date(a.created_at).getTime()
          : 0;
      const tb = ub.last_seen_at
        ? new Date(ub.last_seen_at).getTime()
        : b.created_at
          ? new Date(b.created_at).getTime()
          : 0;
      if (tb !== ta) return tb - ta;
      return getUserDisplayName(ua).localeCompare(getUserDisplayName(ub), undefined, { sensitivity: 'base' });
    };
    const byAlpha = (a, b) =>
      getUserDisplayName(a.sender).localeCompare(getUserDisplayName(b.sender), undefined, {
        sensitivity: 'base',
      });
    rows = [...rows].sort(listSort === 'alpha' ? byAlpha : byRecent);
    return rows;
  }, [receivedInvitations, listQuery, listSort, matchesUserQuery, getUserDisplayName]);

  const processedSent = useMemo(() => {
    let rows = sentInvitations.filter((inv) => matchesUserQuery(inv.receiver, listQuery));
    const byRecent = (a, b) => {
      const ua = a.receiver;
      const ub = b.receiver;
      const ta = ua.last_seen_at
        ? new Date(ua.last_seen_at).getTime()
        : a.created_at
          ? new Date(a.created_at).getTime()
          : 0;
      const tb = ub.last_seen_at
        ? new Date(ub.last_seen_at).getTime()
        : b.created_at
          ? new Date(b.created_at).getTime()
          : 0;
      if (tb !== ta) return tb - ta;
      return getUserDisplayName(ua).localeCompare(getUserDisplayName(ub), undefined, { sensitivity: 'base' });
    };
    const byAlpha = (a, b) =>
      getUserDisplayName(a.receiver).localeCompare(getUserDisplayName(b.receiver), undefined, {
        sensitivity: 'base',
      });
    rows = [...rows].sort(listSort === 'alpha' ? byAlpha : byRecent);
    return rows;
  }, [sentInvitations, listQuery, listSort, matchesUserQuery, getUserDisplayName]);

  useEffect(() => {
    setListQuery('');
  }, [activeTab]);

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const loadInvitations = useCallback(async () => {
    try {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      // Pobierz otrzymane zaproszenia (pending)
      const { data: received, error: receivedError } = await supabase
        .from('friend_invitations')
        .select(`
          id,
          sender_id,
          receiver_id,
          status,
          created_at,
          sender:users!friend_invitations_sender_id_fkey(id, name, nickname, first_name, last_name, avatar_url, city, is_active, last_seen_at, hide_last_activity)
        `)
        .eq('receiver_id', currentUser.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (receivedError) {
        console.error('Error loading received invitations:', receivedError);
      } else {
        setReceivedInvitations(received || []);
      }

      // Pobierz wysłane zaproszenia (pending)
      const { data: sent, error: sentError } = await supabase
        .from('friend_invitations')
        .select(`
          id,
          sender_id,
          receiver_id,
          status,
          created_at,
          receiver:users!friend_invitations_receiver_id_fkey(id, name, nickname, first_name, last_name, avatar_url, city, is_active, last_seen_at, hide_last_activity)
        `)
        .eq('sender_id', currentUser.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (sentError) {
        console.error('Error loading sent invitations:', sentError);
      } else {
        setSentInvitations(sent || []);
      }
    } catch (error) {
      console.error('Error loading invitations:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const loadFriends = useCallback(async () => {
    try {
      if (!currentUser) return;
      
      const result = await getFriends(currentUser.id);
      if (result.success) {
        setFriends(result.data || []);
      }
    } catch (error) {
      console.error('Error loading friends:', error);
    }
  }, [currentUser]);

  const setupRealtimeSubscription = useCallback(() => {
    if (!currentUser) return;

    // Clean up existing subscription
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Subscribe to changes in friend_invitations where user is sender or receiver
    const channel = supabase
      .channel(`friends-management-realtime-${currentUser.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'friend_invitations',
          filter: `sender_id=eq.${currentUser.id}`
        },
        async (payload) => {
          // Przeładuj dane gdy nastąpi zmiana
          await loadInvitations();
          await loadFriends();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'friend_invitations',
          filter: `receiver_id=eq.${currentUser.id}`
        },
        async (payload) => {
          // Przeładuj dane gdy nastąpi zmiana
          await loadInvitations();
          await loadFriends();
        }
      )
      .subscribe();

    channelRef.current = channel;
  }, [currentUser, loadInvitations, loadFriends]);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadInvitations();
      loadFriends();
    }

    return () => {
      // Cleanup subscription
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [currentUser, loadInvitations, loadFriends]);

  // Setup realtime subscription after initial load
  useEffect(() => {
    if (currentUser && !loading) {
      setupRealtimeSubscription();
    }

    return () => {
      // Cleanup subscription
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [currentUser, loading, setupRealtimeSubscription]);

  const handleAcceptInvitation = async (invitationId) => {
    if (processing) return;
    
    setProcessing(invitationId);
    try {
      const result = await acceptFriendInvitation(invitationId);
      if (result.success) {
        await loadInvitations();
        await loadFriends();
      } else {
        alert(t('profile.friends.errorAcceptInvitation') + ': ' + result.error);
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert(t('profile.friends.errorAcceptInvitation'));
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectInvitation = async (invitationId) => {
    if (processing) return;
    
    setProcessing(invitationId);
    try {
      const result = await rejectFriendInvitation(invitationId);
      if (result.success) {
        await loadInvitations();
      } else {
        alert(t('profile.friends.errorRejectInvitation') + ': ' + result.error);
      }
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      alert(t('profile.friends.errorRejectInvitation'));
    } finally {
      setProcessing(null);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (processing) return;
    
    setProcessing(invitationId);
    try {
      const result = await cancelFriendInvitation(invitationId);
      if (result.success) {
        await loadInvitations();
      } else {
        alert(t('profile.friends.errorCancelInvitation') + ': ' + result.error);
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      alert(t('profile.friends.errorCancelInvitation'));
    } finally {
      setProcessing(null);
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (processing) return;
    
    if (!window.confirm(t('profile.friends.confirmRemoveFriend'))) {
      return;
    }

    setProcessing(friendId);
    try {
      const result = await removeFriend(friendId);
      if (result.success) {
        await loadFriends();
        await loadInvitations();
      } else {
        alert(t('profile.friends.errorRemoveFriend') + ': ' + result.error);
      }
    } catch (error) {
      console.error('Error removing friend:', error);
      alert(t('profile.friends.errorRemoveFriend'));
    } finally {
      setProcessing(null);
    }
  };

  const handleUserClick = (userId) => {
    if (onClose) onClose();
    openPopup('profile', { userId }, 0);
  };

  const lastSeenDisplayValue = useCallback(
    (user) => {
      if (user.hide_last_activity) return t('profile.friends.lastSeenHidden');
      if (!user.last_seen_at) return t('profile.friends.lastSeenUnknown');
      return formatProfileLastSeenPhrase(user.last_seen_at, t, i18n) || t('profile.friends.lastSeenUnknown');
    },
    [t, i18n],
  );

  const getUserLampTone = useCallback(
    (user) => {
      if (!user?.last_seen_at || user.hide_last_activity) return 'offline';
      return getProfileLastSeenLampTone(user.last_seen_at);
    },
    [],
  );

  if (loading) {
    return (
      <div className="friends-management-popup">
        <Loading size="medium" />
      </div>
    );
  }

  return (
    <div className="friends-management-popup">
      <div className="friends-management-tabs">
        <button
          className={`friends-management-tab ${activeTab === 'received' ? 'active' : ''}`}
          onClick={() => setActiveTab('received')}
        >
          <span className="friends-management-tab-label">{t('profile.friends.receivedInvitations')}</span>
          {receivedInvitations.length > 0 && (
            <span className="friends-management-tab-badge">{receivedInvitations.length}</span>
          )}
        </button>
        <button
          className={`friends-management-tab ${activeTab === 'sent' ? 'active' : ''}`}
          onClick={() => setActiveTab('sent')}
        >
          <span className="friends-management-tab-label">{t('profile.friends.sentInvitations')}</span>
          {sentInvitations.length > 0 && (
            <span className="friends-management-tab-badge">{sentInvitations.length}</span>
          )}
        </button>
        <button
          className={`friends-management-tab ${activeTab === 'friends' ? 'active' : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          <span className="friends-management-tab-label">{t('profile.friends.title')}</span>
          {friends.length > 0 && (
            <span className="friends-management-tab-badge">{friends.length}</span>
          )}
        </button>
      </div>

      <div className="friends-management-content">
        {activeTab === 'received' && (
          <div className="friends-management-list">
            {receivedInvitations.length === 0 ? (
              <p className="friends-management-empty">{t('profile.friends.noReceivedInvitations')}</p>
            ) : (
              <>
                <div className="friends-management-list-toolbar">
                  <input
                    type="search"
                    className="friends-management-search-input"
                    value={listQuery}
                    onChange={(e) => setListQuery(e.target.value)}
                    placeholder={t('profile.friends.searchPlaceholder')}
                    aria-label={t('profile.friends.searchPlaceholder')}
                  />
                  <select
                    className="friends-management-sort-select"
                    value={listSort}
                    onChange={(e) => setListSort(e.target.value)}
                    aria-label={t('profile.friends.sortRecent')}
                  >
                    <option value="recent">{t('profile.friends.sortRecent')}</option>
                    <option value="alpha">{t('profile.friends.sortAlpha')}</option>
                  </select>
                </div>
                {processedReceived.length === 0 ? (
                  <p className="friends-management-empty">{t('profile.friends.noSearchResults')}</p>
                ) : (
                  processedReceived.map((invitation) => {
                    const user = invitation.sender;
                    const seen = lastSeenDisplayValue(user);
                    const lampTone = getUserLampTone(user);
                    return (
                      <div key={invitation.id} className="friends-management-item">
                        <div className="friends-management-item-row">
                          <div
                            className="friends-management-item-user"
                            onClick={() => handleUserClick(user.id)}
                          >
                            <UserListAvatar
                              user={user}
                              className="friends-management-item-avatar"
                              variantWrapperClassName="friends-management-item-avatar-placeholder"
                              inactiveTitle={t('events.details.avatarInactiveAria')}
                              bannedTitle={t('events.details.avatarBannedAria')}
                              alt={getUserDisplayName(user)}
                              fallback="👤"
                            />
                            <div className="friends-management-item-info">
                              <div className="friends-management-item-name">{getUserDisplayName(user)}</div>
                              {user.city && (
                                <div className="friends-management-item-city">📍 {user.city}</div>
                              )}
                            </div>
                          </div>
                          <div className="friends-management-item-actions">
                            <button
                              type="button"
                              className="friends-management-button friends-management-button-accept"
                              onClick={() => handleAcceptInvitation(invitation.id)}
                              disabled={processing === invitation.id}
                            >
                              {processing === invitation.id ? '...' : t('profile.friends.accept')}
                            </button>
                            <button
                              type="button"
                              className="friends-management-button friends-management-button-reject"
                              onClick={() => handleRejectInvitation(invitation.id)}
                              disabled={processing === invitation.id}
                            >
                              {processing === invitation.id ? '...' : t('profile.friends.reject')}
                            </button>
                          </div>
                        </div>
                        <div
                          className={`friends-management-item-last-seen is-${lampTone}`}
                          aria-label={`${t('profile.popup.lastSeenLabel')}: ${seen}`}
                        >
                          <span className="friends-management-last-seen-lamp" aria-hidden="true" />
                          <span className="friends-management-item-last-seen-label">
                            {t('profile.popup.lastSeenLabel')}:
                          </span>{' '}
                          <span className="friends-management-item-last-seen-value">{seen}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'sent' && (
          <div className="friends-management-list">
            {sentInvitations.length === 0 ? (
              <p className="friends-management-empty">{t('profile.friends.noSentInvitations')}</p>
            ) : (
              <>
                <div className="friends-management-list-toolbar">
                  <input
                    type="search"
                    className="friends-management-search-input"
                    value={listQuery}
                    onChange={(e) => setListQuery(e.target.value)}
                    placeholder={t('profile.friends.searchPlaceholder')}
                    aria-label={t('profile.friends.searchPlaceholder')}
                  />
                  <select
                    className="friends-management-sort-select"
                    value={listSort}
                    onChange={(e) => setListSort(e.target.value)}
                    aria-label={t('profile.friends.sortRecent')}
                  >
                    <option value="recent">{t('profile.friends.sortRecent')}</option>
                    <option value="alpha">{t('profile.friends.sortAlpha')}</option>
                  </select>
                </div>
                {processedSent.length === 0 ? (
                  <p className="friends-management-empty">{t('profile.friends.noSearchResults')}</p>
                ) : (
                  processedSent.map((invitation) => {
                    const user = invitation.receiver;
                    const seen = lastSeenDisplayValue(user);
                    const lampTone = getUserLampTone(user);
                    return (
                      <div key={invitation.id} className="friends-management-item">
                        <div className="friends-management-item-row">
                          <div
                            className="friends-management-item-user"
                            onClick={() => handleUserClick(user.id)}
                          >
                            <UserListAvatar
                              user={user}
                              className="friends-management-item-avatar"
                              variantWrapperClassName="friends-management-item-avatar-placeholder"
                              inactiveTitle={t('events.details.avatarInactiveAria')}
                              bannedTitle={t('events.details.avatarBannedAria')}
                              alt={getUserDisplayName(user)}
                              fallback="👤"
                            />
                            <div className="friends-management-item-info">
                              <div className="friends-management-item-name">{getUserDisplayName(user)}</div>
                              {user.city && (
                                <div className="friends-management-item-city">📍 {user.city}</div>
                              )}
                            </div>
                          </div>
                          <div className="friends-management-item-actions">
                            <button
                              type="button"
                              className="friends-management-button friends-management-button-cancel"
                              onClick={() => handleCancelInvitation(invitation.id)}
                              disabled={processing === invitation.id}
                            >
                              {processing === invitation.id ? '...' : t('profile.friends.cancel')}
                            </button>
                          </div>
                        </div>
                        <div
                          className={`friends-management-item-last-seen is-${lampTone}`}
                          aria-label={`${t('profile.popup.lastSeenLabel')}: ${seen}`}
                        >
                          <span className="friends-management-last-seen-lamp" aria-hidden="true" />
                          <span className="friends-management-item-last-seen-label">
                            {t('profile.popup.lastSeenLabel')}:
                          </span>{' '}
                          <span className="friends-management-item-last-seen-value">{seen}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'friends' && (
          <div className="friends-management-list">
            {friends.length === 0 ? (
              <div className="friends-management-empty">
                <p>{t('profile.friends.noFriends')}</p>
                <p className="friends-management-empty-description">{t('profile.friends.noFriendsDescription')}</p>
              </div>
            ) : (
              <>
                <div className="friends-management-list-toolbar">
                  <input
                    type="search"
                    className="friends-management-search-input"
                    value={listQuery}
                    onChange={(e) => setListQuery(e.target.value)}
                    placeholder={t('profile.friends.searchPlaceholder')}
                    aria-label={t('profile.friends.searchPlaceholder')}
                  />
                  <select
                    className="friends-management-sort-select"
                    value={listSort}
                    onChange={(e) => setListSort(e.target.value)}
                    aria-label={t('profile.friends.sortRecent')}
                  >
                    <option value="recent">{t('profile.friends.sortRecent')}</option>
                    <option value="alpha">{t('profile.friends.sortAlpha')}</option>
                  </select>
                </div>
                {processedFriends.length === 0 ? (
                  <p className="friends-management-empty">{t('profile.friends.noSearchResults')}</p>
                ) : (
                  processedFriends.map((friend) => {
                    const seen = lastSeenDisplayValue(friend);
                    const lampTone = getUserLampTone(friend);
                    return (
                      <div key={friend.id} className="friends-management-item">
                        <div className="friends-management-item-row">
                          <div
                            className="friends-management-item-user"
                            onClick={() => handleUserClick(friend.id)}
                          >
                            <UserListAvatar
                              user={friend}
                              className="friends-management-item-avatar"
                              variantWrapperClassName="friends-management-item-avatar-placeholder"
                              inactiveTitle={t('events.details.avatarInactiveAria')}
                              bannedTitle={t('events.details.avatarBannedAria')}
                              alt={getUserDisplayName(friend)}
                              fallback="👤"
                            />
                            <div className="friends-management-item-info">
                              <div className="friends-management-item-name">{getUserDisplayName(friend)}</div>
                              {friend.city && (
                                <div className="friends-management-item-city">📍 {friend.city}</div>
                              )}
                            </div>
                          </div>
                          <div className="friends-management-item-actions">
                            <button
                              type="button"
                              className="friends-management-button friends-management-button-remove"
                              onClick={() => handleRemoveFriend(friend.id)}
                              disabled={processing === friend.id}
                            >
                              {processing === friend.id ? '...' : t('profile.popup.removeFriend')}
                            </button>
                          </div>
                        </div>
                        <div
                          className={`friends-management-item-last-seen is-${lampTone}`}
                          aria-label={`${t('profile.popup.lastSeenLabel')}: ${seen}`}
                        >
                          <span className="friends-management-last-seen-lamp" aria-hidden="true" />
                          <span className="friends-management-item-last-seen-label">
                            {t('profile.popup.lastSeenLabel')}:
                          </span>{' '}
                          <span className="friends-management-item-last-seen-value">{seen}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FriendsManagementPopup;
